import { useMemo } from 'react';
import { type CamelCasedPropertiesDeep } from 'type-fest';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { keysCamelCaseModifier } from 'hb-react/shared/utils/axiosInterceptors';
import { type AppPage, type Features } from 'hb-react/types';

interface AppPageWithMenu {
  menu: AppPage;
  submenus: Array<AppPage>;
}

interface UseFeatureReturnBase {
  appPageConfig: AppPage | undefined;
  featurePage: AppPageWithMenu | undefined;
}

interface UseFeatureReturn<T extends keyof Features>
  extends UseFeatureReturnBase {
  feature: Features[T];
}

interface UseFeatureCamelCaseReturn<T extends keyof Features>
  extends UseFeatureReturnBase {
  feature: CamelCasedPropertiesDeep<Features[T]>;
}

// Overload signatures
export function useFeature<T extends keyof Features>(
  feature: T,
  options?: { toCamelCase?: false },
): UseFeatureReturn<T>;
export function useFeature<T extends keyof Features>(
  feature: T,
  options: { toCamelCase: true },
): UseFeatureCamelCaseReturn<T>;
// Implementation
export function useFeature<T extends keyof Features>(
  feature: T,
  { toCamelCase = false }: { toCamelCase?: boolean } = {},
): UseFeatureCamelCaseReturn<T> | UseFeatureReturn<T> {
  const {
    currentNetwork: { features, app_pages: appPages = [] },
  } = useDefaultAppContexts();

  const featurePages = useMemo(
    () =>
      appPages
        .flatMap((page) => {
          const subMenuFakePages =
            page?.submenus?.map((subMenu) => ({
              menu: subMenu,
            })) || [];

          return [page, ...subMenuFakePages];
        })
        .filter(Boolean),
    [appPages],
  );

  return useMemo(() => {
    const featurePage = featurePages.find(
      (page) => page?.menu?.identifier === feature,
    );

    const featureValue = features?.[feature] || {};

    const result = {
      appPageConfig: featurePage?.menu,
      feature: toCamelCase ? keysCamelCaseModifier(featureValue) : featureValue,
      featurePage,
    };

    return result as UseFeatureCamelCaseReturn<T> | UseFeatureReturn<T>;
  }, [features, feature, toCamelCase, featurePages]);
}
